import { useEffect, useRef, useState } from 'react';

/**
 * The current epoch ms, re-read every `intervalMs` — a live clock for
 * elapsed-time readouts. `intervalMs <= 0` freezes it (no timer), so an idle
 * panel never re-renders on a tick.
 */
export function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  const previousInterval = useRef(intervalMs);
  useEffect(() => {
    const previous = previousInterval.current;
    previousInterval.current = intervalMs;
    if (intervalMs <= 0) return;
    const update = () => setNow(Date.now());
    // The state initializer already sampled the clock for the first effect.
    // Resample immediately only when a mounted consumer turns ticking on or
    // changes cadence (for example an idle build panel becoming active).
    if (previous !== intervalMs) update();
    const id = setInterval(update, intervalMs);
    // Browsers throttle or fully freeze timers in background tabs. Refresh on
    // lifecycle return as well, so a days-old relative label cannot survive a
    // restored tab until the next interval happens to run.
    document.addEventListener('visibilitychange', update);
    window.addEventListener('focus', update);
    window.addEventListener('pageshow', update);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', update);
      window.removeEventListener('focus', update);
      window.removeEventListener('pageshow', update);
    };
  }, [intervalMs]);
  return now;
}
